// 📄 pages/Proyectos/components/RegistroHorasFormModal.tsx
import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import {
  createRegistro,
  updateRegistro,
  RegistroHoras,
  RegistroHorasFormData
} from '../../../services/registroHorasService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';

interface RegistroHorasFormModalProps {
  projectId: string;
  uid: string;
  solucionesActivas: string[];
  // Proyecto sin SAP Ariba: el campo es "Área" (opcional) en vez de "Solución relacionada".
  modoArea?: boolean;
  registro: RegistroHoras | null; // null = crear, con valor = editar
  onClose: () => void;
  onSaved: () => void;
}

const toDateInputValue = (registro: RegistroHoras | null): string => {
  const fecha = registro?.fecha?.toDate?.();
  if (!fecha) return new Date().toISOString().slice(0, 10);
  return fecha.toISOString().slice(0, 10);
};

export default function RegistroHorasFormModal({ projectId, uid, solucionesActivas, modoArea = false, registro, onClose, onSaved }: RegistroHorasFormModalProps) {
  const editando = !!registro;

  const [form, setForm] = useState<RegistroHorasFormData>({
    fecha: toDateInputValue(registro),
    solucionRelacionada: registro?.solucionRelacionada || (modoArea ? '' : solucionesActivas[0] || ''),
    horas: registro?.horas ?? 1,
    huboSesion: registro?.huboSesion ?? false,
    asistentes: registro?.asistentes || '',
    cambiosRealizados: registro?.cambiosRealizados || '',
    conclusiones: registro?.conclusiones || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = <K extends keyof RegistroHorasFormData>(field: K, value: RegistroHorasFormData[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.fecha) { setError("La fecha es obligatoria."); return; }
    if (!modoArea && solucionesActivas.length > 0 && !form.solucionRelacionada) { setError("Selecciona la solución relacionada."); return; }
    if (!form.horas || form.horas <= 0) { setError("Las horas deben ser mayores a 0."); return; }

    setSaving(true);
    try {
      if (editando && registro) {
        await updateRegistro(projectId, registro.id, form);
      } else {
        await createRegistro(projectId, uid, form);
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || "Error al guardar el registro de horas.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[rgb(var(--c-surface))]">
          <h3 className="text-lg font-medium text-white">{editando ? 'Editar Registro de Horas' : 'Registrar Horas'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Fecha <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={form.fecha}
                onChange={(e) => handleChange('fecha', e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Horas <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                step="0.25"
                min="0"
                value={form.horas}
                onChange={(e) => handleChange('horas', parseFloat(e.target.value) || 0)}
                placeholder="ej. 1.5"
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {solucionesActivas.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              {modoArea ? 'Área' : <>Solución relacionada <span className="text-red-400">*</span></>}
            </label>
            <select
              value={form.solucionRelacionada}
              onChange={(e) => handleChange('solucionRelacionada', e.target.value)}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
            >
              {modoArea && <option value="">Sin área</option>}
              {solucionesActivas.map(slug => (
                <option key={slug} value={slug}>{SOLUCIONES[slug]?.label || slug}</option>
              ))}
            </select>
          </div>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={form.huboSesion}
              onChange={(e) => handleChange('huboSesion', e.target.checked)}
              className="w-4 h-4 rounded border-white/10 bg-zinc-800 accent-blue-500"
            />
            Hubo sesión con el cliente
          </label>

          {form.huboSesion && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Asistentes</label>
              <input
                type="text"
                value={form.asistentes}
                onChange={(e) => handleChange('asistentes', e.target.value)}
                placeholder="Nombres de los asistentes, separados por coma"
                className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Cambios realizados</label>
            <textarea
              value={form.cambiosRealizados}
              onChange={(e) => handleChange('cambiosRealizados', e.target.value)}
              rows={4}
              placeholder="Qué se hizo en este tiempo..."
              className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Conclusiones</label>
            <textarea
              value={form.conclusiones}
              onChange={(e) => handleChange('conclusiones', e.target.value)}
              rows={3}
              placeholder="Conclusiones, pendientes, próximos pasos..."
              className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : editando ? 'Guardar cambios' : 'Registrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
